/**
 * v3.21-1736 · CommunicationsSmsTab
 *
 * SMS surface inside Communications → Inbox.  Reads authorized SMS
 * conversations from the canonical /api/sms-dashboard/conversations
 * endpoint (server enforces Deal Ownership SSoT via
 * services.sms_conversation_service.build_conversation_visibility_query
 * → services.deal_access.build_deal_access_query).  Rows deep-link to
 * /sms-dashboard?conversation_id=… for full thread handling.
 *
 * sms_messages remains the message-body SSoT — this tab never
 * duplicates content.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, MessageSquare, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';

const maskPhone = (v) => {
  const d = String(v || '').replace(/\D/g, '');
  if (d.length >= 4) return `xxx-xxx-${d.slice(-4)}`;
  return v || '—';
};
const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return '—'; }
};

export default function CommunicationsSmsTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sms-dashboard/conversations',
        { params: { filter, q: q || undefined, limit: 100 } });
      setRows(data?.conversations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'SMS load failed');
    } finally { setLoading(false); }
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="communications-sms-panel">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-blue-600" />
        <h2 className="text-sm font-heading font-semibold text-slate-900">SMS Conversations</h2>
        <div className="ml-auto flex items-center gap-2">
          <select
            className="h-8 text-xs border border-slate-300 rounded px-2"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="communications-sms-filter">
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="matched">Matched</option>
            <option value="needs_review">Needs Review</option>
            <option value="unknown_sender">Unknown Sender</option>
            <option value="failed">Failed</option>
          </select>
          <Input
            className="h-8 text-xs w-56"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search subject / participant…"
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            data-testid="communications-sms-search" />
          <Button size="sm" variant="outline" onClick={load}
                  data-testid="communications-sms-refresh"
                  className="h-8">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
          <Button size="sm" onClick={() => navigate('/sms-dashboard')}
                  data-testid="communications-sms-open-dashboard"
                  className="h-8 text-xs">
            Open SMS Dashboard
          </Button>
        </div>
      </div>
      <div className="border border-slate-200 rounded-md divide-y divide-slate-100 bg-white">
        {rows.length === 0 && !loading && (
          <div className="text-xs text-slate-400 italic p-4">No SMS conversations in this view.</div>
        )}
        {rows.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => navigate(`/sms-dashboard?conversation_id=${c.id}`)}
            data-testid={`communications-sms-row-${c.id}`}
            className="w-full text-left px-3 py-2 hover:bg-slate-50 transition flex items-center gap-3">
            <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded
                              ${c.match_state === 'matched' ? 'bg-emerald-100 text-emerald-800'
                                : c.match_state === 'needs_review' ? 'bg-amber-100 text-amber-800'
                                : 'bg-slate-200 text-slate-700'}`}>
              {c.match_state || '—'}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate">
                {c.subject_client_name || <span className="italic text-slate-400">Unassigned</span>}
                {c.credit_application_number && (
                  <span className="text-[11px] text-slate-500 ml-2 font-mono">CA# {c.credit_application_number}</span>
                )}
              </div>
              <div className="text-xs text-slate-600 truncate">
                {c.participant_name || '—'}
                {c.participant_type ? ` · ${c.participant_type.replace(/_/g, ' ')}` : ''}
                <span className="mx-1 text-slate-400">·</span>
                <span className="font-mono">{maskPhone(c.participant_phone)}</span>
              </div>
              <div className="text-[11px] text-slate-500 truncate">
                {c.last_message_direction === 'outbound' ? '↑ ' : '↓ '}{c.last_message_preview || '(no preview)'}
              </div>
            </div>
            <div className="text-right">
              {c.unread_count > 0 && (
                <span className="inline-flex text-[10px] font-bold text-white bg-rose-600 rounded-full h-5 min-w-5 px-1 items-center justify-center">
                  {c.unread_count}
                </span>
              )}
              <div className="text-[10px] text-slate-400 mt-0.5">{formatWhen(c.last_message_at)}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
